import { Drawer, Fade } from "@mui/material";
import styles from "../../content/pages/homepage.module.css";
import { Box } from "@mui/system";
import { useContext, useEffect, useState, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { PaymentMethodMenu } from "./PaymentMethodMenu";
import BillingHistory from "./BillingHistory";
import { ManageMembership } from "./ManageMembership";
import AddPaymentMethod from "./AddPaymentMethod";
import StripeCards from "./StripeCards";
import { isAndroid, isIOS } from "src/utils/platformUtils";
import { WidgetContext } from "src/contexts/WidgetContext";
import CancelMembership from "./CancelMembership";
import PaymentDialog from "./PaymentDialog";
import { getProductConfig } from "src/config/products.config";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { getCredits } from "src/api/getCredits";
import { getActiveSubscription } from "src/api/getActiveSubscription";
import { subscribeFrontRuntimeConfig } from "src/services/runtimeFrontConfig";
import { trackPaywallViewed, trackPurchase, trackSubscriptionFailed } from "src/utils/analyticsCapFirebase";

function paywallTriggerForOpen(
  initialView: string | undefined,
  isRequired: boolean,
): "exceeded_free_credits" | "locked_feature_click" | "upgrade_button" | null {
  const view = initialView || "menu";
  if (view === "cancelMembership") return null;
  // Forced plans: out of credits (agent/trade/chatbot, auto-open, Go Premium at 0)
  if (isRequired) return "exceeded_free_credits";
  // Upgrade CTA / Membership menu / header — user opened it
  return "upgrade_button";
}

function PaymentMethodSwipeable({ onClose, isPaymentOpen, onPaymentOpenChange, initialView, isRequired = false, onDeleteAccountClick, userSubscription }) {
  const { credits, setCredits, freeCredits, setFreeCredits, seasonPassPrice, aiSettings } = useContext(WidgetContext);
  const { user } = useAuth0();
  const analyticsAuthId = user?.sub || "unknown";
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null); // polling interval for intelligent polling for credit updates
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null); // polling timeout for intelligent polling for credit updates
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
  //State for render PaymentMethodMenu if activeComponent is "menu"
  const [activeComponent, setActiveComponent] = useState("menu");
  // Store selected productId to pass to AddPaymentMethod
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  // Store promotion code ID to pass to AddPaymentMethod
  const [promotionCodeId, setPromotionCodeId] = useState<string | null>(null);
  // Store discount info to pass to AddPaymentMethod
  const [promoPercentOff, setPromoPercentOff] = useState<number | null>(null);
  const [promoAmountOff, setPromoAmountOff] = useState<number | null>(null);
  // Store promo code text and discount description for persistence across navigation
  const [promoCode, setPromoCode] = useState<string>("");
  const [promoDiscount, setPromoDiscount] = useState<string | null>(null);
  // Store subscription data for CancelMembership
  const [subscriptionData, setSubscriptionData] = useState<{ expiryDate: string | null; hasActiveSubscription: boolean } | null>(null);
  // Payment dialog states for Stripe Checkout redirects
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentDialogSuccess, setPaymentDialogSuccess] = useState(false);
  const [paymentDialogMessage, setPaymentDialogMessage] = useState<string | undefined>(undefined);
  const prevIsPaymentOpen = useRef(isPaymentOpen);

  const [, setFrontRuntimeBump] = useState(0);
  useEffect(() => subscribeFrontRuntimeConfig(() => setFrontRuntimeBump((n) => n + 1)), []);

  useEffect(() => {
    // Only set activeComponent from initialView when drawer first opens (isPaymentOpen changes from false to true)
    // Don't override if drawer is already open and we're navigating internally
    const wasClosed = !prevIsPaymentOpen.current && isPaymentOpen;
    prevIsPaymentOpen.current = isPaymentOpen;

    if (wasClosed) {
      // Drawer just opened - set component based on initialView
      setActiveComponent(initialView || "menu");
      const trigger = paywallTriggerForOpen(initialView, isRequired);
      if (trigger) {
        void trackPaywallViewed(analyticsAuthId, trigger, "plans");
      }
    }
  }, [isPaymentOpen, initialView, isRequired, analyticsAuthId]);

  useEffect(() => {
    // Only reset to menu if payment drawer is explicitly closed
    // Don't reset when just changing content within an open drawer
    if (!isPaymentOpen && activeComponent !== "menu") {
      setActiveComponent("menu");
    }
    // Clear promo code state when drawer is closed (not just navigating back)
    if (!isPaymentOpen) {
      setPromoCode("");
      setPromoDiscount(null);
      setPromotionCodeId(null);
      setPromoPercentOff(null);
      setPromoAmountOff(null);
    }
  }, [isPaymentOpen, activeComponent]);

  // Check for payment redirect parameters in URL (Stripe Checkout redirects from Web)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get("payment");
    const productId = urlParams.get("productId");

    if (paymentStatus === "success" || paymentStatus === "cancelled") {
      console.log("🔄 Payment redirect detected (Web):", { paymentStatus, productId });

      // Determine success/failure
      const isSuccess = paymentStatus === "success";
      setPaymentDialogSuccess(isSuccess);

      if (isSuccess) {
        const productConfig = productId === "league_connect_season_pass" ? getProductConfig(productId, seasonPassPrice || "1") : getProductConfig(productId || "");
        void trackPurchase(analyticsAuthId, "stripe_checkout", productId || "unknown", productConfig?.price ?? 0);
      } else {
        void trackSubscriptionFailed(analyticsAuthId, productId || "unknown", "abandoned", "user_cancelled");
      }

      // Get product info if productId is available
      if (productId && isSuccess) {
        // For season_pass, use seasonPassPrice from context
        const productConfig = productId === "league_connect_season_pass" ? getProductConfig(productId, seasonPassPrice || "1") : getProductConfig(productId);
        if (productConfig) {
          // Don't show price here since user already saw final price (with any discounts) in Stripe Checkout
          const productMessage = `You purchased ${productConfig.displayName} ${productConfig.creditsAmount} Credits`;
          setPaymentDialogMessage(productMessage);
        } else {
          setPaymentDialogMessage("Your payment has been processed successfully.");
        }
      } else if (!isSuccess) {
        setPaymentDialogMessage("Your payment was cancelled.");
      } else {
        setPaymentDialogMessage("Your payment has been processed successfully.");
      }

      // Show dialog
      setShowPaymentDialog(true);

      // Clean URL - remove query parameters
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      // If payment was successful, fetch updated credits from backend
      // The webhook should have already processed the payment and updated MongoDB
      if (isSuccess) {
        console.log("✅ Payment successful, fetching updated credits from backend...");
        (async () => {
          try {
            // Wait a bit for webhook to process (Stripe webhooks are usually fast but not instant)
            await new Promise((resolve) => setTimeout(resolve, 1000));

            const accessToken = await getSafeAccessAuth0Token();
            if (accessToken) {
              const creditsResponse = await getCredits(accessToken);
              console.log("✅ Credits fetched from backend:", creditsResponse.credits, "freeCredits:", creditsResponse.freeCredits);
              setCredits(creditsResponse.credits);
              setFreeCredits(creditsResponse.freeCredits);
            }
          } catch (error) {
            console.error("❌ Failed to fetch updated credits:", error);
            // Don't show error to user - they'll see updated credits on next page load
          }
        })();
      }
    }
  }, [getSafeAccessAuth0Token, setCredits]); // Run once on mount

  // Listen for Stripe payment completion event (for Capacitor native apps)
  useEffect(() => {
    const handlePaymentComplete = async (event: CustomEvent) => {
      console.log("🔔 Received stripePaymentComplete event:", event.detail);

      const { success, productId } = event.detail;

      // Close the payment drawer
      console.log("Closing payment drawer after Capacitor payment");
      onClose();
      onPaymentOpenChange?.(false);

      // Set dialog state
      setPaymentDialogSuccess(success);

      // Set message
      if (productId && success) {
        // For season_pass, use seasonPassPrice from context
        const productConfig = productId === "league_connect_season_pass" ? getProductConfig(productId, seasonPassPrice || "1") : getProductConfig(productId);
        if (productConfig) {
          const productMessage = `You purchased ${productConfig.displayName} ${productConfig.creditsAmount} Credits`;
          setPaymentDialogMessage(productMessage);
        } else {
          setPaymentDialogMessage("Your payment has been processed successfully.");
        }
      } else if (!success) {
        setPaymentDialogMessage("Your payment was cancelled.");
      } else {
        setPaymentDialogMessage("Your payment has been processed successfully.");
      }

      // Show dialog
      setShowPaymentDialog(true);

      // If payment was successful, start intelligent polling to detect credit changes
      if (success) {
        console.log("✅ PaymentMethodSwipeable: Payment successful, starting intelligent polling for credit updates...");

        // Clear any existing polling first
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }

        // Store current credits value before polling starts
        const initialCredits = credits;
        console.log(`📊 PaymentMethodSwipeable: Initial credits value: ${initialCredits}`);

        let isPollingActive = true;

        // Poll every 2 seconds to check for credit updates
        pollingIntervalRef.current = setInterval(async () => {
          if (!isPollingActive) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            return;
          }

          try {
            const accessToken = await getSafeAccessAuth0Token();
            if (accessToken) {
              const creditsResponse = await getCredits(accessToken);
              const newCredits = creditsResponse.credits;
              console.log(`🔄 PaymentMethodSwipeable: Polling - Current credits in MongoDB: ${newCredits}, UI credits: ${initialCredits}`);

              // If credits increased (MongoDB was updated), update UI and stop polling
              if (newCredits > initialCredits) {
                console.log(`✅ PaymentMethodSwipeable: Credits updated in MongoDB! ${initialCredits} → ${newCredits}. Updating UI...`);
                setCredits(newCredits);
                setFreeCredits(creditsResponse.freeCredits);
                isPollingActive = false;
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }
                if (pollingTimeoutRef.current) {
                  clearTimeout(pollingTimeoutRef.current);
                  pollingTimeoutRef.current = null;
                }
                console.log("✅ PaymentMethodSwipeable: Polling stopped - credits successfully updated");
              }
            } else {
              console.warn("⚠️ PaymentMethodSwipeable: No access token available during polling");
            }
          } catch (error) {
            console.error("❌ PaymentMethodSwipeable: Error during polling:", error);
          }
        }, 2000); // Poll every 2 seconds

        // Stop polling after 60 seconds maximum (safety timeout)
        pollingTimeoutRef.current = setTimeout(() => {
          if (isPollingActive) {
            console.log("⏰ PaymentMethodSwipeable: Polling timeout reached (60s), stopping...");
            isPollingActive = false;
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        }, 60000);
      }
    };

    // Add event listener
    window.addEventListener("stripePaymentComplete", handlePaymentComplete as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener("stripePaymentComplete", handlePaymentComplete as EventListener);
      // Clean up any active polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [getSafeAccessAuth0Token, setCredits, onPaymentOpenChange, credits]);

  // Listen for FREE plan activation event (for Capacitor native apps)
  useEffect(() => {
    const handleFreePlanActivated = async (event: CustomEvent) => {
      console.log("🔔 Received freePlanActivated event:", event.detail);

      const { success, message } = event.detail;

      // Close the payment drawer
      console.log("Closing payment drawer after FREE plan activation");
      onClose();
      onPaymentOpenChange?.(false);

      // Set dialog state
      setPaymentDialogSuccess(success);
      setPaymentDialogMessage(message || "FREE plan activated successfully!");

      // Show dialog
      setShowPaymentDialog(true);

      //  NOTE: Do NOT fetch credits here - HomePage.tsx handles state updates (credits + subscription)
      // This component only handles UI (dialog + closing drawer)
      // if (success) {
      //   console.log("✅ FREE plan activated, fetching updated credits from backend...");
      //   try {
      //     // Wait for backend to process
      //     await new Promise((resolve) => setTimeout(resolve, 1000));

      //     const accessToken = await getSafeAccessAuth0Token();
      //     if (accessToken) {
      //       const creditsResponse = await getCredits(accessToken);
      //       console.log("✅ Credits fetched from backend:", creditsResponse.credits);
      //       setCredits(creditsResponse.credits);
      //     }
      //   } catch (error) {
      //     console.error("❌ Failed to fetch updated credits:", error);
      //   }
      // }
    };

    // Add event listener
    window.addEventListener("freePlanActivated", handleFreePlanActivated as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener("freePlanActivated", handleFreePlanActivated as EventListener);
    };
  }, [getSafeAccessAuth0Token, setCredits, onPaymentOpenChange, onClose]);

  const handleClose = () => {
    // Don't allow closing if this is a required paywall (user has 0 credits)
    // if (isRequired) {
    //   return;
    // }
    const timer = setTimeout(() => {
      onClose();
      onPaymentOpenChange?.(false);
      setActiveComponent("menu");
      setSelectedProductId(null); // Clear selected product when closing
      setPromotionCodeId(null); // Clear promotion code when closing
      setPromoPercentOff(null); // Clear discount info when closing
      setPromoAmountOff(null); // Clear discount info when closing
      setPromoCode(""); // Clear promo code text when closing
      setPromoDiscount(null); // Clear discount description when closing
    }, 100);
    return () => clearTimeout(timer);
  };

  // Go back to the menu
  const handleBack = () => {
    setActiveComponent("menu");
    // onPaymentOpenChange?.(false);
    // Don't close the drawer, just go back to menu
    setSelectedProductId(null); // Clear selected product when going back
    // Keep promo code state when going back - user may want to use it again
    // Only clear when drawer is fully closed (handleClose)
  };

  const handleChangeContent = (nextContent: string, data?: any) => {
    setActiveComponent(nextContent);
    // Store subscription data if provided (for cancelMembership)
    if (nextContent === "cancelMembership" && data) {
      setSubscriptionData(data);
    }
    // Only change payment open state for addMethod, keep drawer open for other content changes
    if (nextContent === "addMethod") {
      onPaymentOpenChange?.(true);
    }
  };

  // Function to open AddPaymentMethod - replicates HomePage button behavior
  const handleOpenAddPaymentMethod = (productId?: string, promoCodeId?: string | null, percentOff?: number | null, amountOff?: number | null) => {
    console.log(
      "🔍 PaymentMethodSwipeable - handleOpenAddPaymentMethod called with productId:",
      productId,
      "promoCodeId:",
      promoCodeId,
      "percentOff:",
      percentOff,
      "amountOff:",
      amountOff
    );
    if (productId) {
      setSelectedProductId(productId);
      console.log("🔍 PaymentMethodSwipeable - Set selectedProductId to:", productId);
    } else {
      console.warn("⚠️ PaymentMethodSwipeable - No productId provided!");
    }
    if (promoCodeId) {
      setPromotionCodeId(promoCodeId);
      console.log("🔍 PaymentMethodSwipeable - Set promotionCodeId to:", promoCodeId);
    }
    if (percentOff !== undefined) {
      setPromoPercentOff(percentOff);
      console.log("🔍 PaymentMethodSwipeable - Set promoPercentOff to:", percentOff);
    }
    if (amountOff !== undefined) {
      setPromoAmountOff(amountOff);
      console.log("🔍 PaymentMethodSwipeable - Set promoAmountOff to:", amountOff);
    }
    setActiveComponent("addMethod");
    onPaymentOpenChange?.(true);
  };

  const renderContent = () => {
    switch (activeComponent) {
      case "addMethod":
        return (
          <AddPaymentMethod
            onClose={onClose}
            onBack={handleBack}
            onChangeContent={handleChangeContent}
            setActiveComponent={setActiveComponent}
            onPaymentOpenChange={onPaymentOpenChange}
            selectedProductId={selectedProductId}
            promotionCodeId={promotionCodeId}
            promoPercentOff={promoPercentOff}
            promoAmountOff={promoAmountOff}
          />
        );
      case "billingHistory":
        return <BillingHistory onClose={onClose} onBack={handleBack} />;
      case "manageMembership":
        return (
          <ManageMembership
            onClose={onClose}
            onBack={handleBack}
            isRequired={isRequired}
            onOpenAddPaymentMethod={handleOpenAddPaymentMethod}
            promoCode={promoCode}
            promoCodeId={promotionCodeId}
            promoDiscount={promoDiscount}
            promoPercentOff={promoPercentOff}
            promoAmountOff={promoAmountOff}
            onPromoCodeChange={setPromoCode}
            onPromoCodeIdChange={setPromotionCodeId}
            onPromoDiscountChange={setPromoDiscount}
            onPromoPercentOffChange={setPromoPercentOff}
            onPromoAmountOffChange={setPromoAmountOff}
            onOptionSelected={
              isRequired
                ? () => {
                  // DEV: Add test credits to enable endpoints again
                  // In production, this will be handled by the payment flow
                  // setCredits(1); // Add 1 test credit
                  // Allow closing when an option is selected
                  onClose();
                  onPaymentOpenChange?.(false);
                  setActiveComponent("menu");
                }
                : undefined
            }
            userSubscription={userSubscription}
          />
        );
      case "cancelMembership":
        return <CancelMembership onClose={onClose} onBack={handleBack} subscriptionData={subscriptionData} />;
      case "stripeCards":
      // return <StripeCards onClose={onClose} onBack={() => setActiveComponent("addMethod")} />;
      default:
        return <PaymentMethodMenu onClose={onClose} onBack={handleBack} onChangeContent={handleChangeContent} onCancelMembershipClick={(data) => setSubscriptionData(data)} onDeleteAccountClick={onDeleteAccountClick} userSubscription={userSubscription} />;
    }
  };

  return (
    <>
      <Drawer
        anchor="bottom"
        open={isPaymentOpen}
        // onClose={isRequired ? undefined : handleClose}
        onClose={handleClose}
        hideBackdrop={!isRequired}
        sx={{ pointerEvents: "none" }}
        ModalProps={{
          keepMounted: true,
          // No cubrir la barra superior: safe-area + 56px (misma cifra que restas en height)
          sx: {
            pointerEvents: "auto", // Block background scroll when drawer is open
            top: isIOS || isAndroid ? "calc(env(safe-area-inset-top) + 56px)" : "56px",
            zIndex: 1500, // highest priority: PaymentMethod appears above response drawers and BenchDrawer
          },
        }}
        PaperProps={{
          sx: {
            pointerEvents: "auto",
            width: {
              xs: "100%",
              sm: "720px",
              md: "720px",
              lg: "720px",
            },
            margin: {
              xs: "0",
              sm: "0 auto",
              md: "0 auto",
              lg: "0 auto",
            },
            // height: isIOS || isAndroid ? "calc(100svh - env(safe-area-inset-top) - 56px)" : "calc(100vh  - 56px)",
            height: isIOS || isAndroid
              ? (aiSettings.quan ? "calc(100svh - env(safe-area-inset-top))" : "calc(100svh - env(safe-area-inset-top) - 56px)")
              : (!aiSettings.quan ? "calc(100vh - 56px)" : "100%"),
            border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            bgcolor: "#EDEDED",
            zIndex: 1500, // highest priority: PaymentMethod appears above response drawers and BenchDrawer
          },
        }}
      >
        <Fade in={isPaymentOpen}>
          <Box className={styles.noScrollbar} sx={{ bgcolor: `#EDEDED`, opacity: isPaymentOpen ? 1 : 0, transition: "opacity 0.5s" }}>
            {renderContent()}
          </Box>
        </Fade>
      </Drawer>

      {/* Payment Dialog for Stripe Checkout redirects */}
      <PaymentDialog
        open={showPaymentDialog}
        onClose={() => {
          setShowPaymentDialog(false);
          // If payment was successful, close the drawer
          if (paymentDialogSuccess) {
            handleClose();
          }
        }}
        success={paymentDialogSuccess}
        message={paymentDialogMessage}
        autoCloseDelay={4000}
      />
    </>
  );
}

export default PaymentMethodSwipeable;
